import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { InputAdornment, MenuItem, Autocomplete as MuiAutoComplete, Paper, Popper, autocompleteClasses, styled } from '@mui/material'
import { TextField } from '../Form/TextField'
import { Icon } from '../Icon/Icon'

export type EditableDropdownOption = {
  name: string
  text: string
}

export type EditableDropdownProps = {
  options: EditableDropdownOption[]
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  placeholder?: string
}

export const EditableDropdown: React.FC<EditableDropdownProps> = ({
  options,
  value,
  onChange,
  disabled,
  placeholder = 'Select or type destination',
}) => {
  return (
    <StyledAutocomplete
      freeSolo
      options={options}
      value={options.find(opt => opt.text === value) || value}
      inputValue={value}
      disabled={disabled}
      PaperComponent={StyledPaper}
      PopperComponent={StyledPopper}
      disablePortal
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return option
        }

        return (option as EditableDropdownOption).text
      }}
      isOptionEqualToValue={(option, val) => {
        if (typeof val === 'string') {
          return (option as EditableDropdownOption).text === val
        }

        return (option as EditableDropdownOption).text === (val as EditableDropdownOption).text
      }}
      onInputChange={(_, newInputValue) => {
        onChange(newInputValue)
      }}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          onChange(newValue)
        } else if (newValue) {
          onChange((newValue as EditableDropdownOption).text)
        } else {
          onChange('')
        }
      }}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            InputProps={{
              ...params.InputProps,
              placeholder: placeholder,
              endAdornment: (
                <InputAdornment position="end">
                  <StyledDropdownIcon icon={faChevronDown} />
                </InputAdornment>
              ),
            }}
          />
        )
      }}
      renderOption={(optionProps, option) => {
        return (
          <StyledDropdownItem
            {...optionProps}
            key={(option as EditableDropdownOption).name}
          >
            {(option as EditableDropdownOption).text}
          </StyledDropdownItem>
        )
      }}
    />
  )
}

const StyledDropdownIcon = styled(Icon)`
  color: ${props => props.theme.palette.text.primary};
  margin-right: ${props => props.theme.typography.pxToRem(16)};
  font-size: ${props => props.theme.typography.pxToRem(14)};
`

const StyledPopper = styled(Popper)`
  z-index: ${(props) => props.theme.zIndex.appBar + 110};
  background-color: ${(props) => props.theme.palette.background.paper};
  border-radius: ${(props) => props.theme.typography.pxToRem(8)};
`

const StyledPaper = styled(Paper)`
  box-shadow: none;
  border-radius: ${(props) => props.theme.typography.pxToRem(8)};
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.15);
  margin: 0 !important;
  padding: 0 !important;

  &.MuiAutocomplete-listbox {
    margin: 0 !important;
    padding: 0 !important;
  }
`

const StyledAutocomplete = styled(MuiAutoComplete)`
  .MuiInputBase-root {
    padding: 0 !important;

    .MuiOutlinedInput-input {
      padding: ${(props) => props.theme.typography.pxToRem(12.5)} ${(props) => props.theme.typography.pxToRem(12)};
    }
  }
`

const StyledDropdownItem = styled(MenuItem)`
  padding: 0;
  margin: 0;
  z-index: 2;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${props => props.theme.palette.text.primary};

  &.${autocompleteClasses.option} {
    padding: ${(props) => props.theme.typography.pxToRem(8)} ${(props) => props.theme.typography.pxToRem(16)};
    transition: opacity 0.2s ease;

    ${props => props.theme.breakpoints.up('sm')} {
      padding: ${props => props.theme.typography.pxToRem(16)} ${props => props.theme.typography.pxToRem(16)};
    }

    &:after {
      content: '';
      position: absolute;
      z-index: 1;
      top: 0;
      bottom: 0;
      right: 0;
      left: 0;
      background-color: ${props => props.theme.palette.custom.button.textButtonHoverBackground};
      opacity: 0;
      transition: opacity 0.2s ease;
    }

    &:hover {
      background-color: none !important;

      &:after {
        opacity: 1;
      }
    }
  }
`
